import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/section.default.js';
import * as wireframeSkin from './skins/section.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

class RunSection extends RunElement {
  #heading = '';
  #level = 2;
  #anchor = '';
  #divider = false;
  #rendering = false;
  #connected = false;
  #registeredAnchor = '';

  static get observedAttributes() {
    return ['heading', 'level', 'anchor', 'divider'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#connected = true;
    this.setAttribute('role', 'region');
    const resolved = this.#resolveAnchor();
    this.id = resolved;
    this.#registeredAnchor = resolved;
    this.#updateAria();
    this.#emitRegister(resolved);
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#connected = false;
    this.#emitUnregister(this.#registeredAnchor);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'heading': this.heading = newValue !== null ? newValue : ''; break;
      case 'level':   this.level   = newValue !== null ? Number(newValue) : 2; break;
      case 'anchor':  this.anchor  = newValue !== null ? newValue : ''; break;
      case 'divider': this.divider = newValue !== null; break;
    }
  }

  get heading() { return this.#heading; }
  set heading(value) {
    const coerced = String(value == null ? '' : value).trim();
    if (this.#heading === coerced) return;
    if (this.#connected) {
      const oldAnchor = this.#registeredAnchor;
      this.#heading = coerced;
      this.#emitUnregister(oldAnchor);
      const newAnchor = this.#resolveAnchor();
      this.id = newAnchor;
      this.#registeredAnchor = newAnchor;
      this.#updateAria();
      this.#emitRegister(newAnchor);
    } else {
      this.#heading = coerced;
    }
    this.requestUpdate();
  }

  get level() { return this.#level; }
  set level(value) {
    const n = Number(value);
    const coerced = (n === 2 || n === 3 || n === 4) ? n : 2;
    if (this.#level === coerced) return;
    this.#level = coerced;
    this.setAttribute('level', String(coerced));
    this.#updateAria();
    this.requestUpdate();
  }

  get anchor() { return this.#anchor; }
  set anchor(value) {
    const coerced = String(value == null ? '' : value).trim();
    if (this.#anchor === coerced) return;
    if (this.#connected) {
      const oldAnchor = this.#registeredAnchor;
      this.#anchor = coerced;
      this.#emitUnregister(oldAnchor);
      const newAnchor = this.#resolveAnchor();
      this.id = newAnchor;
      this.#registeredAnchor = newAnchor;
      this.#updateAria();
      this.#emitRegister(newAnchor);
    } else {
      this.#anchor = coerced;
    }
    this.requestUpdate();
  }

  get divider() { return this.#divider; }
  set divider(value) {
    const coerced = Boolean(value);
    if (this.#divider === coerced) return;
    this.#divider = coerced;
    this.toggleAttribute('divider', coerced);
    this.requestUpdate();
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #resolveAnchor() {
    if (this.#anchor) return this.#anchor;
    return this.#heading
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  #updateAria() {
    this.setAttribute('role', 'region');
    if (this.#heading) {
      this.setAttribute('aria-labelledby', 'section-heading');
    } else {
      this.removeAttribute('aria-labelledby');
    }
  }

  #emitRegister(anchor) {
    this.dispatchEvent(new CustomEvent('run-section-register', {
      bubbles: true,
      composed: true,
      detail: {
        anchor,
        heading: this.#heading,
        level: this.#level,
        element: this,
      },
    }));
  }

  #emitUnregister(anchor) {
    // When disconnectedCallback fires the element is already detached — no ancestors to
    // bubble through. Dispatch from ownerDocument so listeners on document still receive it.
    const target = this.isConnected ? this : (this.ownerDocument || document);
    target.dispatchEvent(new CustomEvent('run-section-unregister', {
      bubbles: true,
      composed: true,
      detail: { anchor },
    }));
  }
}

export const SECTION_LEVELS = Object.freeze([2, 3, 4]);
export const SECTION_LEVEL_DEFAULT = 2;

if (!customElements.get('run-section')) {
  customElements.define('run-section', RunSection);
}

export { RunSection };
export default RunSection;